import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CalculatorIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChartBarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  PieChart,
  Pie,
  AreaChart,
  Area
} from 'recharts';

interface FilterProps {
  dateRange: { start: string; end: string; preset: string };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface BudgetVarianceProps {
  filters: FilterProps;
}

const BudgetVariance: React.FC<BudgetVarianceProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedView, setSelectedView] = useState('overview');
  const [timeframe, setTimeframe] = useState('monthly');

  // Budget variance summary
  const varianceSummary = [
    {
      title: 'إجمالي الميزانية',
      budgeted: 4200000,
      actual: 3891000,
      variance: -309000,
      variancePercent: -7.4,
      status: 'under',
      icon: CalculatorIcon,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'الإيرادات',
      budgeted: 2800000,
      actual: 2847500,
      variance: 47500,
      variancePercent: 1.7,
      status: 'over',
      icon: ArrowTrendingUpIcon,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'المصروفات التشغيلية',
      budgeted: 1400000,
      actual: 1043500,
      variance: -356500,
      variancePercent: -25.5,
      status: 'under',
      icon: ChartBarIcon,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'الاستثمارات',
      budgeted: 600000,
      actual: 650000,
      variance: 50000,
      variancePercent: 8.3,
      status: 'over',
      icon: CurrencyDollarIcon,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  // Department budget variance
  const departmentVariance = [
    {
      department: 'التسويق الرقمي',
      budgeted: 850000,
      actual: 624000,
      variance: -226000,
      variancePercent: -26.6,
      status: 'under',
      reason: 'تأجيل حملات إعلانية',
      forecast: 780000,
      ytdBudget: 850000,
      ytdActual: 624000
    },
    {
      department: 'المبيعات',
      budgeted: 720000,
      actual: 648000,
      variance: -72000,
      variancePercent: -10.0,
      status: 'under',
      reason: 'توفير في مصاريف السفر',
      forecast: 700000,
      ytdBudget: 720000,
      ytdActual: 648000
    },
    {
      department: 'التطوير التقني',
      budgeted: 950000,
      actual: 741500,
      variance: -208500,
      variancePercent: -21.9,
      status: 'under',
      reason: 'تأخير في توظيف مطورين',
      forecast: 850000,
      ytdBudget: 950000,
      ytdActual: 741500
    },
    {
      department: 'الموارد البشرية',
      budgeted: 480000,
      actual: 432000,
      variance: -48000,
      variancePercent: -10.0,
      status: 'under',
      reason: 'تأجيل برامج التدريب',
      forecast: 460000,
      ytdBudget: 480000,
      ytdActual: 432000
    },
    {
      department: 'المالية والمحاسبة',
      budgeted: 380000,
      actual: 342000,
      variance: -38000,
      variancePercent: -10.0,
      status: 'under',
      reason: 'توفير في الاستشارات',
      forecast: 365000,
      ytdBudget: 380000,
      ytdActual: 342000
    },
    {
      department: 'خدمة العملاء',
      budgeted: 320000,
      actual: 304000,
      variance: -16000,
      variancePercent: -5.0,
      status: 'under',
      reason: 'كفاءة في العمليات',
      forecast: 315000,
      ytdBudget: 320000,
      ytdActual: 304000
    }
  ];

  // Monthly variance trend
  const monthlyVariance = [
    { month: 'يناير', budgeted: 350000, actual: 324500, variance: -25500, cumulativeVariance: -25500 },
    { month: 'فبراير', budgeted: 350000, actual: 338000, variance: -12000, cumulativeVariance: -37500 },
    { month: 'مارس', budgeted: 350000, actual: 367500, variance: 17500, cumulativeVariance: -20000 },
    { month: 'أبريل', budgeted: 350000, actual: 341250, variance: -8750, cumulativeVariance: -28750 },
    { month: 'مايو', budgeted: 350000, actual: 366667, variance: 16667, cumulativeVariance: -12083 },
    { month: 'يونيو', budgeted: 350000, actual: 387917, variance: 37917, cumulativeVariance: 25834 }
  ];

  // Variance categories
  const varianceCategories = [
    { category: 'رواتب وأجور', budgeted: 1800000, actual: 1750000, variance: -50000 },
    { category: 'التسويق والإعلان', budgeted: 600000, actual: 450000, variance: -150000 },
    { category: 'التكنولوجيا', budgeted: 400000, actual: 380000, variance: -20000 },
    { category: 'المرافق والإيجار', budgeted: 300000, actual: 295000, variance: -5000 },
    { category: 'السفر والانتقال', budgeted: 200000, actual: 120000, variance: -80000 },
    { category: 'التدريب والتطوير', budgeted: 150000, actual: 95000, variance: -55000 },
    { category: 'الاستشارات', budgeted: 180000, actual: 140000, variance: -40000 },
    { category: 'أخرى', budgeted: 170000, actual: 160000, variance: -10000 }
  ];

  // Budget alerts
  const budgetAlerts = [
    {
      type: 'critical',
      title: 'تجاوز ميزانية الاستثمارات',
      description: 'تم تجاوز ميزانية الاستثمارات بنسبة 8.3% (50,000 ر.س)',
      department: 'التطوير التقني',
      amount: 50000,
      action: 'مراجعة فورية مطلوبة'
    },
    {
      type: 'warning',
      title: 'انخفاض كبير في مصاريف التسويق',
      description: 'انخفاض بنسبة 26.6% عن المخطط (226,000 ر.س)',
      department: 'التسويق الرقمي',
      amount: -226000,
      action: 'تقييم تأثير على الأهداف'
    },
    {
      type: 'info',
      title: 'توفير في المصاريف التشغيلية',
      description: 'توفير 25.5% من المصاريف التشغيلية (356,500 ر.س)',
      department: 'عام',
      amount: -356500,
      action: 'إعادة تخصيص الميزانية'
    },
    {
      type: 'positive',
      title: 'تجاوز الإيرادات المستهدفة',
      description: 'تجاوز الإيرادات المستهدفة بنسبة 1.7% (47,500 ر.س)',
      department: 'المبيعات',
      amount: 47500,
      action: 'مواصلة الاستراتيجية الحالية'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'over':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-300';
      case 'under':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-300';
      case 'on-track':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'critical':
        return 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20';
      case 'info':
        return 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20';
      case 'positive':
        return 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20';
      default:
        return 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800';
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return XCircleIcon;
      case 'warning':
        return ExclamationTriangleIcon;
      case 'info':
        return ClockIcon;
      case 'positive':
        return CheckCircleIcon;
      default:
        return ClockIcon;
    }
  };

  const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];

  return (
    <div className="space-y-6">
      {/* Budget Variance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {varianceSummary.map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-8 h-8 bg-gradient-to-r ${item.color} rounded-lg p-1.5`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                  {item.status === 'over' ? 'تجاوز' : item.status === 'under' ? 'أقل' : 'متوازن'}
                </div>
              </div>
              
              <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {item.title}
              </h3>
              
              <div className="space-y-1 mb-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">المخطط:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {(item.budgeted / 1000).toLocaleString()} ألف ر.س
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-400">الفعلي:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {(item.actual / 1000).toLocaleString()} ألف ر.س
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className={`text-sm font-bold ${
                  item.variance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                }`}>
                  {item.variance > 0 ? '+' : ''}{(item.variance / 1000).toLocaleString()} ألف ر.س
                </span>
                <span className={`text-xs font-medium ${
                  item.variance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                }`}>
                  {item.variancePercent > 0 ? '+' : ''}{item.variancePercent.toFixed(1)}%
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Budget Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <ExclamationTriangleIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
          تنبيهات الميزانية
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {budgetAlerts.map((alert, index) => {
            const Icon = getAlertIcon(alert.type);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`border rounded-lg p-4 ${getAlertColor(alert.type)}`}
              >
                <div className="flex items-start space-x-3 rtl:space-x-reverse">
                  <Icon className={`w-5 h-5 mt-0.5 ${
                    alert.type === 'critical' ? 'text-red-600' :
                    alert.type === 'warning' ? 'text-yellow-600' :
                    alert.type === 'positive' ? 'text-green-600' : 'text-blue-600'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {alert.title}
                      </h4>
                      <span className={`text-sm font-bold ${
                        alert.amount > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {alert.amount > 0 ? '+' : ''}{(alert.amount / 1000).toLocaleString()} ألف ر.س
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {alert.description}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">
                        {alert.department}
                      </span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {alert.action}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Monthly Variance Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            اتجاه الانحراف الشهري
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={monthlyVariance}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000).toLocaleString()}ك`}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  `${(value / 1000).toLocaleString()} ألف ر.س`,
                  name === 'budgeted' ? 'المخطط' : 
                  name === 'actual' ? 'الفعلي' :
                  name === 'variance' ? 'الانحراف' : 'الانحراف التراكمي'
                ]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="budgeted" fill="#94A3B8" name="budgeted" radius={[2, 2, 0, 0]} />
              <Bar dataKey="actual" fill="#3B82F6" name="actual" radius={[2, 2, 0, 0]} />
              <Line
                type="monotone"
                dataKey="cumulativeVariance"
                stroke="#EF4444"
                strokeWidth={3}
                name="cumulativeVariance"
                dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
              />
              <ReferenceLine y={0} stroke="#6B7280" strokeDasharray="2 2" />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            انحراف الفئات
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={varianceCategories} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                type="number"
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000).toLocaleString()}ك`}
              />
              <YAxis 
                type="category"
                dataKey="category"
                className="text-xs"
                tick={{ fontSize: 10 }}
                width={100}
              />
              <Tooltip
                formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, 'الانحراف']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="variance" radius={[0, 4, 4, 0]}>
                {varianceCategories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.variance > 0 ? '#EF4444' : '#10B981'} />
                ))}
              </Bar>
              <ReferenceLine x={0} stroke="#6B7280" strokeDasharray="2 2" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Department Variance Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            انحراف الميزانية حسب القسم
          </h3>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="monthly">شهري</option>
            <option value="quarterly">ربع سنوي</option>
            <option value="yearly">سنوي</option>
          </select>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">القسم</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">المخطط</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الفعلي</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">الانحراف</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">النسبة</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">التوقع</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900 dark:text-white">السبب</th>
              </tr>
            </thead>
            <tbody>
              {departmentVariance.map((dept, index) => (
                <motion.tr
                  key={dept.department}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{dept.department}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-medium">
                    {(dept.budgeted / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {(dept.actual / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className={`text-sm font-medium ${
                        dept.variance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                      }`}>
                        {dept.variance > 0 ? '+' : ''}{(dept.variance / 1000).toLocaleString()} ألف ر.س
                      </span>
                      {dept.variance > 0 ? (
                        <ArrowUpIcon className="w-4 h-4 text-red-500" />
                      ) : (
                        <ArrowDownIcon className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-sm font-medium ${
                      dept.variancePercent > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                    }`}>
                      {dept.variancePercent > 0 ? '+' : ''}{dept.variancePercent.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-blue-600 dark:text-blue-400 font-medium">
                    {(dept.forecast / 1000).toLocaleString()} ألف ر.س
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-500 dark:text-gray-400">
                    {dept.reason}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default BudgetVariance;
